// Addressables (spec/dashboard.md section 9.8): the catalogs per platform from `GET …/addressables` — Platform, Catalog
// hash (short, with Copy), Files (count and total bytes), Version; a row expands to its files with the public
// `/content/addressables/sha256/<hash>` link; Delete catalog is a command with a typed-platform confirmation. The
// dashboard never uploads bundles (the Unity plugin does), and a catalog cannot declare a missing hash, so there is no
// "missing" state.
import { Fragment, useState } from 'react';
import { useParams } from 'react-router';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { EmptyState } from '../../ui/Banner';
import { Button, IconButton } from '../../ui/Button';
import { useConfirm } from '../../ui/ConfirmDialog';
import { AlertCircle, ChevronDown, ChevronRight, Copy, Package, RefreshCw, Trash2 } from '../../ui/icons';
import { useToasts } from '../../ui/Toast';
import { useQuery } from '../../workspace/cache';
import { useWorkspace } from '../../workspace/react';
import { copyText, errorText, formatBytes } from './common';

type Catalog = components['schemas']['AddressablesCatalogDto'];

export function AddressablesTab() {
  const { org, game, can } = useGame();
  const { branch = '' } = useParams();
  const { client } = useAuth();
  const workspace = useWorkspace();
  const confirm = useConfirm();
  const toasts = useToasts();
  const canWrite = can('content_write') && !workspace.isProtected;
  const path = { org, game: game.slug, branch };
  const q = useQuery(`branch:${game.id}:${branch}:addressables`, () => client.data(ops.listAddressables, { path }) as unknown as Promise<Catalog[]>, { head: workspace.head });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const catalogs = [...(q.data ?? [])].sort((a, b) => a.platform.localeCompare(b.platform));

  const toggle = (platform: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) next.delete(platform);
      else next.add(platform);
      return next;
    });

  const remove = async (c: Catalog) => {
    const ok = await confirm({
      title: `Delete the ${c.platform} catalog?`,
      body: `The catalog of ${c.files.length} file${c.files.length === 1 ? '' : 's'} leaves the branch (one commit); the bundles stay in the store by hash. The Unity plugin declares it again with its next upload.`,
      typeToConfirm: c.platform,
      confirmLabel: 'Delete catalog',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await client.call(ops.deleteAddressables, { path: { ...path, platform: c.platform } });
      toasts.success(`The ${c.platform} catalog was deleted.`);
    } catch (e) {
      toasts.error(errorText(e), { requestId: e instanceof ApiError ? e.requestId : undefined });
    }
    q.refetch();
  };

  if (q.loading && !q.data)
    return (
      <div className="pane">
        <div className="skeleton-rows" role="status" aria-label="Loading the Addressables catalogs">
          {[0, 1, 2].map((i) => (
            <div key={i} className="skeleton-row">
              <span className="skeleton" />
              <span className="skeleton" />
            </div>
          ))}
        </div>
      </div>
    );
  if (q.error !== undefined && !q.data)
    return (
      <div className="pane">
        <div className="pane-body">
          <EmptyState
            icon={<AlertCircle />}
            title="The Addressables catalogs could not be loaded"
            body={<span role="alert">{errorText(q.error)}</span>}
            action={
              <Button size="s" onClick={q.refetch} icon={<RefreshCw aria-hidden="true" />}>
                Retry
              </Button>
            }
          />
        </div>
      </div>
    );
  if (catalogs.length === 0)
    return (
      <div className="pane">
        <div className="pane-body">
          <EmptyState
            icon={<Package />}
            title="No Addressables catalogs on this branch"
            body={
              <>
                The Unity plugin declares and uploads the bundles: it uploads each file (<code>PUT …/addressables/{'{platform}'}/files/{'{sha256}'}</code>) and then declares the platform's catalog (<code>PUT …/addressables/{'{platform}'}</code>). The dashboard never uploads them; a declared catalog shows here.
              </>
            }
          />
        </div>
      </div>
    );

  return (
    <div className="pane">
      <div className="pane-head">
        <h2>Catalogs</h2>
        <span className="chip">{catalogs.length}</span>
        <span className="spacer" />
        <span className="muted small">Declared by the Unity plugin; one per platform</span>
      </div>
      <table className="grid asset-table" data-testid="addressables-table">
        <thead>
          <tr>
            <th className="asset-col-toggle" aria-label="Expand" />
            <th>Platform</th>
            <th>Catalog hash</th>
            <th>Files</th>
            <th>Version</th>
            <th className="asset-col-actions" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {catalogs.map((c) => {
            const open = expanded.has(c.platform);
            const total = c.files.reduce((sum, f) => sum + Number(f.bytes ?? 0), 0);
            return (
              <Fragment key={c.platform}>
                <tr className={`grid-row asset-catalog-row ${open ? 'open' : ''}`}>
                  <td className="asset-col-toggle">
                    <IconButton label={`Files of ${c.platform}`} aria-expanded={open} onClick={() => toggle(c.platform)}>
                      {open ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
                    </IconButton>
                  </td>
                  <td>
                    <span className="asset-platform">
                      <Package className="icon-s" aria-hidden="true" />
                      <code>{c.platform}</code>
                    </span>
                  </td>
                  <td>
                    <span className="row">
                      <code title={c.catalogHash}>{c.catalogHash.slice(0, 12)}</code>
                      <IconButton label={`Copy the catalog hash of ${c.platform}`} onClick={() => copyText(c.catalogHash, () => toasts.info('Copied.'))}>
                        <Copy aria-hidden="true" />
                      </IconButton>
                    </span>
                  </td>
                  <td className="num">
                    {c.files.length} file{c.files.length === 1 ? '' : 's'} · {formatBytes(total)}
                  </td>
                  <td className="num">{String(c.version)}</td>
                  <td className="asset-col-actions">
                    <Button size="s" variant="danger" icon={<Trash2 aria-hidden="true" />} onClick={() => void remove(c)} missingPermission={can('content_write') ? null : 'content_write'} disabled={!canWrite} title={workspace.isProtected ? 'Protected branch: changes land through merge requests.' : undefined}>
                      Delete catalog
                    </Button>
                  </td>
                </tr>
                {open && (
                  <tr className="asset-subrow">
                    <td />
                    <td colSpan={5}>
                      {c.files.length === 0 ? (
                        <p className="muted small">The catalog declares no files.</p>
                      ) : (
                        <div className="asset-file-tree">
                            <table className="asset-files" aria-label={`Files of ${c.platform}`}>
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>sha256</th>
                                  <th className="num">Bytes</th>
                                  <th>Link</th>
                                </tr>
                              </thead>
                              <tbody>
                                {c.files.map((f) => {
                                  const href = `/content/addressables/sha256/${f.sha256}`;
                                  return (
                                    <tr key={`${f.name}:${f.sha256}`}>
                                      <td>{f.name}</td>
                                      <td>
                                        <code title={f.sha256}>{f.sha256.slice(0, 12)}</code>
                                      </td>
                                      <td className="num">{formatBytes(f.bytes)}</td>
                                      <td>
                                        <a href={href} target="_blank" rel="noreferrer">
                                          {href}
                                        </a>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
